import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IItemPromoPrice } from "@faire/web-api/indigofair/data/IItemPromoPrice";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { moneyOrZero } from "@faire/web/common/money";
import minBy from "lodash/minBy";

import { DEFAULT_CURRENCY } from "@faire/retailer/consts/DEFAULT_CURRENCY";
import {
  isPrePackProduct,
  isPrePackProductOption,
} from "@faire/retailer/features/PrePacks/isPrePackProduct";
import { sum } from "@faire/retailer/lib/sum";

interface IAvailableUnits {
  available_units?: number;
}

export const getOptionAvailableUnits = (
  product: Pick<IProduct, "allow_sales_when_out_of_stock">,
  option?: IAvailableUnits
): number => {
  if (product.allow_sales_when_out_of_stock) {
    return Number.POSITIVE_INFINITY;
  }
  return option?.available_units ?? Number.POSITIVE_INFINITY;
};

// product utilities
export const getProductAvailableUnits = (
  product: getProductAvailableUnits.ILeanProduct
): number =>
  product.options
    ? sum(
        product.options.map((option) =>
          getOptionAvailableUnits(product, option)
        )
      )
    : 0;

export namespace getProductAvailableUnits {
  export interface ILeanProduct
    extends Pick<IProduct, "allow_sales_when_out_of_stock"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<IProductOption, "available_units">;
}

export const getProductImages = (
  product?: getProductImages.ILeanProduct,
  productOptions: getProductImages.ILeanProductOption[] = product?.options ?? []
): IImage[] => {
  if (!product) {
    return [];
  }

  // Used to dedup photos that have the same URL because both the product and the option point to
  // the same image.
  const imagesByType: Record<string, IImage[]> = {
    HERO: [],
    PRODUCT: [],
    PACKAGING: [],
    LIFESTYLE: [],
    OTHER: [],
  };
  const imageUrls: Set<string> = new Set();

  product.images?.forEach((image: IImage) => {
    if (image.url) {
      if (imageUrls.has(image.url)) {
        return;
      }
      if (image.type) {
        imagesByType[image.type]?.push(image);
      } else {
        imagesByType.OTHER?.push(image);
      }
      imageUrls.add(image.url);
    }
  });

  productOptions?.forEach((productOption: Pick<IProductOption, "images">) => {
    productOption.images?.forEach((image: IImage) => {
      if (image.url) {
        if (imageUrls.has(image.url)) {
          return;
        }
        if (image.type) {
          imagesByType[image.type]?.push(image);
        } else {
          imagesByType.OTHER?.push(image);
        }
        imageUrls.add(image.url);
      }
    });
  });

  const imageList =
    imagesByType.HERO?.concat(
      imagesByType.OTHER ?? [],
      imagesByType.PRODUCT ?? [],
      imagesByType.PACKAGING ?? [],
      imagesByType.LIFESTYLE ?? []
    ) ?? [];

  return imageList;
};

export namespace getProductImages {
  export interface ILeanProduct extends Pick<IProduct, "images"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<IProductOption, "images">;
}

export const getBestProductImage = (
  product?: getBestProductImage.ILeanProduct,
  productOptions: getBestProductImage.ILeanProductOption[] = product?.options ??
    []
): IImage | undefined => {
  if (!product) {
    return undefined;
  }

  const imageList = getProductImages(product, productOptions);
  return imageList.length > 0 ? imageList[0] : undefined;
};

export namespace getBestProductImage {
  export interface ILeanProduct extends Pick<IProduct, "images"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<IProductOption, "images">;
}

export const getProductOptionImage = (
  product: getProductOptionImage.ILeanProduct,
  optionToken: string
): IImage | undefined => {
  const option = product.options?.find(
    (option) => option.token === optionToken
  );
  if (option && option.images && option.images.length) {
    return option.images[0];
  }
  return getBestProductImage(product) || undefined;
};

export namespace getProductOptionImage {
  export interface ILeanProduct extends Pick<IProduct, "images"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<IProductOption, "token" | "images">;
}

export const getLeastExpensiveOptionByRetailPrice = (
  options: IProductOption[]
): IProductOption | undefined =>
  minBy(
    options,
    (option) => option.retail_price?.amount_cents ?? option.retail_price_cents
  );

const leastExpensiveOption = <
  T extends getProductLevelPromotion.ILeanProductOption
>(product: {
  options: T[];
}): undefined | T =>
  minBy(
    product.options,
    (option) =>
      option.wholesale_price?.amount_cents ??
      option.wholesale_price_cents ??
      option.retail_price?.amount_cents ??
      option.retail_price_cents
  )!;

export const hasOptions = (product: Pick<IProduct, "options">) =>
  product.options !== undefined && product.options.length > 0;

export const getProductLevelPromotion = (
  product?: getProductLevelPromotion.IProduct,
  productOption?: getProductLevelPromotion.ILeanProductOption
): IItemPromoPrice | undefined => {
  if (!product) {
    return;
  }
  const option = productOption ?? leastExpensiveOption(product);
  return option?.wholesale_promo_price;
};

export namespace getProductLevelPromotion {
  export interface IProduct extends Pick<IProduct, "options"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<
    IProductOption,
    | "wholesale_price"
    | "wholesale_price_cents"
    | "wholesale_promo_price"
    | "retail_price"
    | "retail_price_cents"
    | "unit_count_per_cart_item"
  >;
}

export const getProductOptionProperty = <
  K extends keyof (
    | Pick<IProduct, "options" | "unit_multiplier">
    | IProductOption
  )
>(
  product: Pick<IProduct, "options" | "unit_multiplier">,
  propertyName: K
): (Pick<IProduct, "options" | "unit_multiplier"> | IProductOption)[K] => {
  if (product.options) {
    const option = leastExpensiveOption(product);
    return option?.[propertyName] || product[propertyName];
  }
  return product[propertyName];
};

export const getProductOptionWholesalePrice = (
  product?: getProductOptionWholesalePrice.ILeanProduct,
  productOption?: getProductOptionWholesalePrice.ILeanProductOption
): IFaireMoney => {
  if (!product) {
    return moneyOrZero(undefined);
  }
  const option = productOption ?? leastExpensiveOption(product);
  if (
    option &&
    isPrePackProductOption(option) &&
    option.unit_count_per_cart_item
  ) {
    return IFaireMoney.build({
      amount_cents:
        (option.wholesale_price?.amount_cents ?? 0) /
        option.unit_count_per_cart_item,
      currency: option.wholesale_price?.currency ?? DEFAULT_CURRENCY,
    });
  }
  return (
    option?.wholesale_price ??
    product.max_option_wholesale_price ??
    IFaireMoney.build({
      amount_cents: 0,
      currency: DEFAULT_CURRENCY,
    })
  );
};

export namespace getProductOptionWholesalePrice {
  export interface ILeanProduct
    extends Pick<IProduct, "max_option_wholesale_price"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<
    IProductOption,
    | "token"
    | "type"
    | "unit_count_per_cart_item"
    | "retail_price"
    | "wholesale_price"
    | "wholesale_price_cents"
    | "wholesale_promo_price"
    | "retail_price_cents"
  >;
}

export const getProductOptionRetailPrice = (
  product?: getProductOptionRetailPrice.ILeanProduct,
  productOption?: getProductOptionRetailPrice.ILeanProductOption
): IFaireMoney => {
  if (!product) {
    return moneyOrZero(undefined);
  }
  const option = productOption ?? leastExpensiveOption(product);
  if (
    option &&
    isPrePackProductOption(option) &&
    option.unit_count_per_cart_item
  ) {
    return IFaireMoney.build({
      amount_cents:
        (option.retail_price?.amount_cents ?? 0) /
        option.unit_count_per_cart_item,
      currency: option.retail_price?.currency ?? DEFAULT_CURRENCY,
    });
  }
  return (
    option?.retail_price ??
    product.max_option_retail_price ??
    IFaireMoney.build({
      amount_cents: 0,
      currency: DEFAULT_CURRENCY,
    })
  );
};

export namespace getProductOptionRetailPrice {
  export interface ILeanProduct
    extends Pick<IProduct, "max_option_retail_price"> {
    options: ILeanProductOption[];
  }

  export type ILeanProductOption = Pick<
    IProductOption,
    | "token"
    | "type"
    | "unit_count_per_cart_item"
    | "retail_price"
    | "wholesale_price"
    | "wholesale_price_cents"
    | "wholesale_promo_price"
    | "retail_price_cents"
  >;
}

export const getCaseSizeText = (
  product: Pick<
    IProduct,
    | "per_style_min_order_quantity"
    | "options"
    | "unit_multiplier"
    | "has_pre_packs_available"
  >,
  strictLocalize: StrictLocalizeFunction
): string => {
  const caseSize = getProductOptionProperty(product, "unit_multiplier");

  const prePacksAvailable = isPrePackProduct(product);
  const isOpenSizingProduct =
    !prePacksAvailable && !!product.per_style_min_order_quantity;

  if (prePacksAvailable) {
    return strictLocalize({
      defaultMessage: "Prepacks Available",
      description: {
        text: "Retailers can require the brand prepare a package for the product with a specific number of items or a specific assortment of sizes, colors, flavors, etc",
      },
    });
  }

  if (isOpenSizingProduct) {
    return strictLocalize(
      {
        defaultMessage: "Min Qty {minimumOrderQuantity}",
        description: {
          text: "The minimum quantity of products needed to be included in each order.",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        minimumOrderQuantity: product.per_style_min_order_quantity,
      })
    );
  }

  return strictLocalize(
    {
      defaultMessage: "Case of {caseSize}",
      description: {
        text: "Each case has a size of X products in it",
      },
    },
    DEPRECATED_backwardsCompatibleValues({ caseSize: caseSize })
  );
};
